import { createTheme, Theme } from '@mui/material/styles';

// Color palette
const palette = {
  primary: {
    main: '#1976d2',
    light: '#42a5f5',
    dark: '#1565c0',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#dc004e',
    light: '#ff4081',
    dark: '#c51162',
    contrastText: '#ffffff',
  },
  success: {
    main: '#2e7d32',
    light: '#4caf50',
    dark: '#1b5e20',
    contrastText: '#ffffff',
  },
  error: {
    main: '#d32f2f',
    light: '#ef5350',
    dark: '#c62828',
    contrastText: '#ffffff',
  },
  warning: {
    main: '#ed6c02',
    light: '#ff9800',
    dark: '#e65100',
    contrastText: '#ffffff',
  },
  info: {
    main: '#0288d1',
    light: '#03a9f4',
    dark: '#01579b',
    contrastText: '#ffffff',
  },
};

// Typography with Thai font support
const typography = {
  fontFamily: [
    'Noto Sans Thai',
    'Roboto',
    'Arial',
    'sans-serif',
  ].join(','),
  h1: {
    fontWeight: 700,
    fontSize: '2.5rem',
    letterSpacing: '-0.02em',
  },
  h2: {
    fontWeight: 700,
    fontSize: '2rem',
    letterSpacing: '-0.02em',
  },
  h3: {
    fontWeight: 600,
    fontSize: '1.75rem',
    letterSpacing: '-0.01em',
  },
  h4: {
    fontWeight: 600,
    fontSize: '1.5rem',
    letterSpacing: '-0.01em',
  },
  h5: {
    fontWeight: 600,
    fontSize: '1.25rem',
    letterSpacing: '0em',
  },
  h6: {
    fontWeight: 600,
    fontSize: '1rem',
    letterSpacing: '0.01em',
  },
  subtitle1: {
    fontWeight: 500,
    fontSize: '1rem',
    letterSpacing: '0.01em',
  },
  subtitle2: {
    fontWeight: 500,
    fontSize: '0.875rem',
    letterSpacing: '0.02em',
  },
  body1: {
    fontWeight: 400,
    fontSize: '1rem',
    letterSpacing: '0.02em',
  },
  body2: {
    fontWeight: 400,
    fontSize: '0.875rem',
    letterSpacing: '0.02em',
  },
  button: {
    fontWeight: 500,
    fontSize: '0.875rem',
    letterSpacing: '0.03em',
    textTransform: 'none' as const,
  },
  caption: {
    fontWeight: 400,
    fontSize: '0.75rem',
    letterSpacing: '0.03em',
  },
  overline: {
    fontWeight: 500,
    fontSize: '0.75rem',
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
  },
};

// Component style overrides
const components = {
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        padding: '8px 16px',
        fontSize: '0.875rem',
        fontWeight: 500,
        '&:hover': {
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        },
      },
      contained: {
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
        },
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundImage: 'none',
        borderRadius: 12,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      },
      elevation1: {
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      },
      elevation2: {
        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: 8,
          '&:hover fieldset': {
            borderColor: 'rgba(0, 0, 0, 0.23)',
          },
        },
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        fontWeight: 500,
        fontSize: '0.8125rem',
      },
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: {
        borderRadius: 8,
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: 16,
      },
    },
  },
  MuiTableHead: {
    styleOverrides: {
      root: {
        '& .MuiTableCell-head': {
          fontWeight: 600,
          fontSize: '0.8125rem',
          backgroundColor: 'rgba(0, 0, 0, 0.02)',
        },
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        borderRadius: 0,
        boxShadow: 'none',
      },
    },
  },
};

// Spacing tokens
const spacing = {
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
};

// Border radius tokens
const borderRadius = {
  sm: 4,
  md: 8,
  lg: 12,
  xl: 16,
};

// Shadow tokens
const shadows = {
  card: '0 2px 8px rgba(0,0,0,0.08)',
  elevated: '0 4px 16px rgba(0,0,0,0.12)',
  dialog: '0 8px 32px rgba(0,0,0,0.16)',
};

// Z-index tokens
const zIndex = {
  drawer: 1200,
  dialog: 1300,
  snackbar: 1400,
  tooltip: 1500,
};

// Create theme
const theme: Theme = createTheme({
  palette,
  typography,
  components,
  spacing: 8, // Base spacing unit
  shape: {
    borderRadius: 8,
  },
});

// Export theme and tokens
export default theme;
export { palette, typography, components, spacing, borderRadius, shadows, zIndex };
